import { stringify as stringifyYaml } from 'yaml';

export type YamlValueKind = 'string' | 'number' | 'boolean' | 'null';

export type YamlLine = {
  prefix: string;
  key?: string;
  value?: string;
  kind?: YamlValueKind;
  path: string;
  selectable: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isContainer = (value: unknown) => Array.isArray(value) || isRecord(value);

const isEmptyContainer = (value: unknown) =>
  Array.isArray(value)
    ? value.length === 0
    : isRecord(value) && !Object.keys(value).length;

const appendObjectKey = (path: string, key: string): string => {
  if (/^[A-Za-z_$][\w$]*$/.test(key)) return path ? `${path}.${key}` : key;
  return `${path}[${JSON.stringify(key)}]`;
};

const formatScalar = (
  value: unknown,
): { text: string; kind: YamlValueKind } => {
  if (value === null || value === undefined)
    return { text: 'null', kind: 'null' };
  if (Array.isArray(value)) return { text: '[]', kind: 'null' };
  if (isRecord(value)) return { text: '{}', kind: 'null' };
  if (typeof value === 'number') return { text: String(value), kind: 'number' };
  if (typeof value === 'boolean')
    return { text: String(value), kind: 'boolean' };
  const text = stringifyYaml(String(value)).trim();
  return {
    text: text.includes('\n') ? JSON.stringify(value) : text,
    kind: 'string',
  };
};

/** Flattens an object into YAML-looking lines, each carrying its dot-separated path. */
export const buildYamlLines = (data: unknown): YamlLine[] => {
  const lines: YamlLine[] = [];

  const walk = (
    value: unknown,
    path: string,
    indent: number,
    firstDash: boolean,
  ) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const itemPath = `${path}[${index}]`;
        if (isContainer(item) && !isEmptyContainer(item)) {
          if (Array.isArray(item)) {
            lines.push({
              prefix: `${' '.repeat(indent)}-`,
              path: itemPath,
              selectable: false,
            });
            walk(item, itemPath, indent + 2, false);
          } else {
            walk(item, itemPath, indent + 2, true);
          }
          return;
        }
        const { text, kind } = formatScalar(item);
        lines.push({
          prefix: `${' '.repeat(indent)}- `,
          value: text,
          kind,
          path: itemPath,
          selectable: true,
        });
      });
      return;
    }
    if (isRecord(value)) {
      Object.entries(value).forEach(([key, child], index) => {
        const childPath = appendObjectKey(path, key);
        const prefix =
          firstDash && index === 0
            ? `${' '.repeat(indent - 2)}- `
            : ' '.repeat(indent);
        if (isContainer(child) && !isEmptyContainer(child)) {
          lines.push({ prefix, key, path: childPath, selectable: false });
          walk(child, childPath, indent + 2, false);
          return;
        }
        const { text, kind } = formatScalar(child);
        lines.push({
          prefix,
          key,
          value: text,
          kind,
          path: childPath,
          selectable: true,
        });
      });
    }
  };

  walk(data, '', 0, false);
  return lines;
};
